import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Autocomplete, Stack, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon,
  VerifiedUser as VerifiedIcon, Draw as DrawIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

const DigitalSignature: React.FC = () => {
  const { isMobile, isTabletOrDown } = useClinicPageBreakpoints();
  const [signatures, setSignatures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [profOptions, setProfOptions] = useState<any[]>([]);

  const [form, setForm] = useState<any>({
    professional_id: '', certificate_type: 'e-cpf',
    certificate_number: '', valid_until: '', provider: '',
  });

  useEffect(() => { loadSignatures(); }, [search]);

  const loadSignatures = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 50 };
      if (search) params.search = search;
      const { data } = await axios.get(`${getApiUrl()}/api/clinic/digital-signatures`, { params });
      setSignatures(data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const searchProfessionals = async (q: string) => {
    try { const { data } = await axios.get(`${getApiUrl()}/api/clinic/prescriptions-search/professionals`, { params: { search: q } }); setProfOptions(data.data || []); } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${getApiUrl()}/api/clinic/digital-signatures`, form);
      setSnackbar({ open: true, message: 'Assinatura cadastrada!', severity: 'success' });
      setOpenDialog(false); loadSignatures();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir assinatura?')) return;
    try {
      await axios.delete(`${getApiUrl()}/api/clinic/digital-signatures/${id}`);
      setSnackbar({ open: true, message: 'Excluída!', severity: 'success' }); loadSignatures();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const isExpired = (date: string) => date && new Date(date) < new Date();

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: { xs: 2, md: 3 },
        gap: 2,
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>Assinatura Digital</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Gerencie certificados digitais dos profissionais</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="outlined" fullWidth={isMobile} startIcon={<RefreshIcon />} onClick={loadSignatures} sx={{ textTransform: 'none', borderColor: '#14B8A6', color: '#14B8A6' }}>
            Atualizar
          </Button>
          <Button variant="contained" fullWidth={isMobile} startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }} onClick={() => setOpenDialog(true)}>
            Novo Certificado
          </Button>
        </Box>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: signatures.length, color: '#14B8A6' },
          { label: 'Válidos', value: signatures.filter(s => !isExpired(s.valid_until)).length, color: '#22c55e' },
          { label: 'Expirados', value: signatures.filter(s => isExpired(s.valid_until)).length, color: '#ef4444' },
        ].map((kpi, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card sx={{ bgcolor: 'white', borderRadius: 3, border: `1px solid ${kpi.color}22` }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${kpi.color}15`, color: kpi.color }}>
                  {kpi.label === 'Total' ? <DrawIcon /> : <VerifiedIcon />}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>{kpi.label}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, p: 2, '&:last-child': { pb: 2 } }}>
          <TextField size="small" placeholder="Buscar certificados..." value={search} onChange={(e) => setSearch(e.target.value)} fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        </CardContent>
      </Card>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#14B8A6' }} /></Box>
      ) : isTabletOrDown ? (
        <Stack spacing={1.5}>
          {signatures.length === 0 ? (
            <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center', color: '#94a3b8' }}>Nenhum certificado</Card>
          ) : signatures.map((s) => (
            <Card key={s.id} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{s.professional_name || '-'}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, alignItems: 'center' }}>
                  <Chip label={s.certificate_type?.toUpperCase()} size="small" sx={{ bgcolor: '#14B8A615', color: '#14B8A6', fontWeight: 600, fontSize: '0.7rem' }} />
                  <Chip label={isExpired(s.valid_until) ? 'Expirado' : 'Válido'} size="small"
                    sx={{ bgcolor: isExpired(s.valid_until) ? '#fee2e2' : '#dcfce7', color: isExpired(s.valid_until) ? '#ef4444' : '#22c55e', fontWeight: 600, fontSize: '0.7rem' }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>{s.certificate_number || '—'} · {s.provider || '—'}</Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Validade: {s.valid_until ? new Date(s.valid_until).toLocaleDateString('pt-BR') : '-'}
                </Typography>
                <Divider sx={{ my: 1.25 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={() => handleDelete(s.id)} sx={{ color: '#ef4444' }} aria-label="Excluir"><DeleteIcon /></IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Card sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Profissional</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Número</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Provedor</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Validade</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {signatures.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhum certificado</TableCell></TableRow>
                ) : signatures.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{s.professional_name || '-'}</TableCell>
                    <TableCell><Chip label={s.certificate_type?.toUpperCase()} size="small" sx={{ bgcolor: '#14B8A615', color: '#14B8A6', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                    <TableCell>{s.certificate_number || '-'}</TableCell>
                    <TableCell>{s.provider || '-'}</TableCell>
                    <TableCell>{s.valid_until ? new Date(s.valid_until).toLocaleDateString('pt-BR') : '-'}</TableCell>
                    <TableCell>
                      <Chip label={isExpired(s.valid_until) ? 'Expirado' : 'Válido'} size="small"
                        sx={{ bgcolor: isExpired(s.valid_until) ? '#fee2e2' : '#dcfce7', color: isExpired(s.valid_until) ? '#ef4444' : '#22c55e', fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleDelete(s.id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, borderRadius: 0, height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column' }
            : {},
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, px: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Novo Certificado Digital</Typography>
          <IconButton onClick={() => setOpenDialog(false)} aria-label="Fechar"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete size="small" options={profOptions} getOptionLabel={(o: any) => o.full_name || ''}
                onInputChange={(_, v) => searchProfessionals(v)} onChange={(_, v) => setForm({ ...form, professional_id: v?.id || '' })}
                onOpen={() => searchProfessionals('')} renderInput={(params) => <TextField {...params} label="Profissional" />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Tipo" value={form.certificate_type} onChange={(e) => setForm({ ...form, certificate_type: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Número do Certificado" value={form.certificate_number} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Provedor" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Validade" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 }, flexShrink: 0, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button fullWidth={isMobile} onClick={() => setOpenDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button fullWidth={isMobile} variant="contained" onClick={handleSave} sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}>Cadastrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DigitalSignature;
